// components/PurchaseSchedule.tsx

import {
  Box,
  Flex,
  HStack,
  Icon,
  Select,
  Text,
  Spinner,
  Tab,
  TabList,
  Tabs,
  useToast
} from '@chakra-ui/react'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { FiFilter, FiUsers } from 'react-icons/fi'

import useParametrizationConfigPurchase from '@hook/parametrization/use-config-purchase'
import useFilters from '@hook/shared/use-filters'

import { getPurchaseConfigTokenClaims } from '@helper/parametrization/purchaseConfigAuth'

import { ScheduleGrid } from './GridCalendarPurchase'
import { ScheduleItem } from './types'

// O tipo de agendamento agora inclui 'LOJA'
export type ScheduleType = 'FORNECEDOR' | 'LOJA' | 'ALL' | 'CATEGORIA_COMPACTA'

interface PurchaseScheduleProps {
  data: ScheduleItem[]
}
// Ids temporários de itens novos: negativos e sequenciais, para nunca colidir com ids do backend
// (o id não vai no payload de salvar; serve só para arrastar/remover na tela)
let tempItemId = 0
// Remove o prefixo "id - " de um label de select, ficando só o nome
const stripIdPrefix = (label: string | undefined, id: number): string =>
  (label ?? '').replace(new RegExp(`^${id}\\s*-\\s*`), '').trim()
const nextTempItemId = () => --tempItemId

export const PurchaseSchedule = ({ data }: PurchaseScheduleProps) => {
  const [filterType, setFilterType] = useState<ScheduleType>('ALL')
  const [selected, setSelected] = useState<string>('todos')
  const {
    companieId,
    handleCronogromaCompra,
    deleteCronogromaCompra,
    cronogramaCompra,
    setCronogramaCompra,
    // Estados dos compradores
    compradores,
    selectedComprador,
    loadingCompradores,
    isAdmin,
    getCompradores,
    handleCompradorChange,
    initializeComprador,
    getFornecedoresByComprador,
    selectedCompradorEmail
  } = useParametrizationConfigPurchase()
  const { centersConfig } = useFilters()

  const toast = useToast()
  // Lógica de filtro que agora lida com Fornecedor e Loja
  const filteredData = useMemo(() => {
    if (selected === 'todos') return data

    return data.filter(item => {
      const key =
        filterType === 'FORNECEDOR'
          ? 'fornecedorId'
          : filterType === 'LOJA'
          ? 'lojaId'
          : 'categoriaCompactaId'
      return String(item[key]) === selected
    })
  }, [data, selected, filterType])

  const tokenUserId = useMemo(() => getPurchaseConfigTokenClaims()?.userid, [])

  // Inicializar compradores e configurações. initializeComprador vem antes:
  // define o perfil/comprador padrão que getCompradores consulta (via token/ref)
  useEffect(() => {
    initializeComprador?.()
    getCompradores?.()
    // Só na montagem: getCompradores muda de identidade com a empresa e recarregaria a lista à toa
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Carregar fornecedores quando comprador e empresa estiverem disponíveis
  useEffect(() => {
    if (!selectedComprador || !companieId || !getFornecedoresByComprador) return
    // Ignora a resposta se o comprador/empresa mudar antes de ela chegar
    let cancelled = false
    getFornecedoresByComprador(Number(companieId), selectedComprador)
      .then(fornecedoresData => {
        if (!cancelled && fornecedoresData) setDataFornecedor(fornecedoresData)
      })
      .catch(() => {
        if (!cancelled) setDataFornecedor([])
      })
    return () => {
      cancelled = true
    }
  }, [selectedComprador, companieId, getFornecedoresByComprador])
  // Gera opções de filtro dinamicamente com base na aba selecionada
  const options = useMemo(() => {
    if (filterType === 'ALL') return []

    const isFornecedor = filterType === 'FORNECEDOR'
    const keyId = isFornecedor
      ? 'fornecedorId'
      : filterType === 'LOJA'
      ? 'lojaId'
      : 'categoriaCompactaId'
    const keyName = isFornecedor
      ? 'fornecedorNome'
      : filterType === 'LOJA'
      ? 'lojaNome'
      : 'categoriaCompactaNome'

    const uniqueItems = new Map<number, string>()
    data.forEach(item => {
      if (!uniqueItems.has(item[keyId])) {
        uniqueItems.set(item[keyId], item[keyName])
      }
    })

    return Array.from(uniqueItems.entries()).map(([value, label]) => ({
      value: String(value),
      label: `${label} (#${value})`
    }))
  }, [data, filterType])

  const [dataFornecedor, setDataFornecedor] = useState<any>([])

  const [categoriasFornecedor, setCategoriasFornecedor] = useState<
    { value: number; label: string }[]
  >([])

  const handleFornecedorChange = (fornecedorId: number) => {
    const fornecedor = dataFornecedor.find(
      (f: any) => f.fornecedorId === fornecedorId
    )
    if (fornecedor && fornecedor.categoriasCompactas) {
      setCategoriasFornecedor(
        fornecedor.categoriasCompactas.map((c: any) => ({
          value: c.categoriaCompactaId,
          label: `${c.categoriaCompactaId} - ${c.categoriaCompactaNome}`
        }))
      )
    } else {
      setCategoriasFornecedor([])
    }
  }
  const fornecedoresList: any[] = useMemo(() => {
    const unique = new Map<number, string>()
    dataFornecedor?.forEach((item: any) =>
      unique.set(item.fornecedorId, item.fornecedorNome)
    )
    return Array.from(unique.entries()).map(([value, label]) => ({
      value,
      label: `${value} - ${label}`
    }))
  }, [dataFornecedor])

  const handleDragEnd = (itemId: number, newDay: number) => {
    if (setCronogramaCompra) {
      setCronogramaCompra(prev =>
        prev.map(item =>
          item.id === itemId ? { ...item, diaDaSemana: newDay } : item
        )
      )
    }
  }

  const handleLocalCompradorChange = (
    event: ChangeEvent<HTMLSelectElement>
  ) => {
    const value = parseInt(event.target.value)
    if (value && handleCompradorChange) {
      handleCompradorChange(value)
    }
  }

  return (
    <Box>
      {/* Filtro de Comprador - sempre visível para admins */}
      {isAdmin && (
        <Box
          bg="white"
          p={4}
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
          shadow="sm"
          mb={4}
        >
          <HStack spacing={4} align="center">
            <HStack spacing={2}>
              <Icon as={FiUsers} color="blue.500" />
              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                Comprador:
              </Text>
            </HStack>

            <Flex flex={1} maxW="300px">
              {loadingCompradores ? (
                <HStack spacing={2}>
                  <Spinner size="sm" color="blue.500" />
                  <Text fontSize="sm" color="gray.500">
                    Carregando compradores...
                  </Text>
                </HStack>
              ) : (
                <Box w="full">
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    {compradores.length} comprador(es) carregado(s)
                  </Text>
                  <Select
                    value={selectedComprador || ''}
                    onChange={handleLocalCompradorChange}
                    placeholder="Selecione um comprador"
                    size="sm"
                    bg="white"
                    borderColor="gray.300"
                    _hover={{
                      borderColor: 'blue.400'
                    }}
                    _focus={{
                      borderColor: 'blue.500',
                      boxShadow: '0 0 0 1px #3182ce'
                    }}
                  >
                    {compradores.map((comprador, index) => {
                      return (
                        <option
                          key={`${comprador.id}-${index}`}
                          value={comprador.id}
                          style={{
                            color: 'black',
                            backgroundColor: 'white',
                            padding: '8px'
                          }}
                        >
                          {comprador.nome}{' '}
                          {comprador.email ? `(${comprador.email})` : ''}
                        </option>
                      )
                    })}
                  </Select>
                </Box>
              )}
            </Flex>

            {/* {selectedComprador && (
              <Badge colorScheme="blue" size="sm">
                Selecionado
              </Badge>
            )} */}
          </HStack>
        </Box>
      )}

      <Flex align="center" gap={4} mb={4}>
        <Tabs
          variant="soft-rounded"
          colorScheme="green"
          onChange={i => {
            if (i === 0) setFilterType('ALL')
            if (i === 1) setFilterType('FORNECEDOR')
            if (i === 2) setFilterType('LOJA')
            if (i === 3) setFilterType('CATEGORIA_COMPACTA')
            setSelected('todos') // Reseta o filtro ao trocar de aba
          }}
        >
          <TabList>
            <Tab>Todos</Tab>
            <Tab>Fornecedor</Tab>
            <Tab>Loja</Tab>
            <Tab>Categoria Compacta</Tab>
          </TabList>
        </Tabs>

        {filterType !== 'ALL' && (
          <Flex
            align="center"
            border="1px solid"
            borderColor="gray.300"
            borderRadius="md"
            px={2}
            py={1}
            maxW="300px"
            w="full"
          >
            <FiFilter style={{ marginRight: 8 }} />
            <Select
              variant="unstyled"
              value={selected}
              onChange={e => setSelected(e.target.value)}
              bg="white"
              color="black"
              borderColor="gray.300"
              focusBorderColor="green.500"
              size="md"
              boxShadow="sm"
            >
              <option value="todos">Todos</option>
              {options.map(opt => (
                <option
                  key={opt.value}
                  value={opt.value}
                  style={{
                    backgroundColor: 'white',
                    color: 'black'
                  }}
                >
                  {opt.label}
                </option>
              ))}
            </Select>
          </Flex>
        )}
      </Flex>

      <ScheduleGrid
        data={filteredData}
        type={filterType}
        lojas={centersConfig}
        fornecedores={fornecedoresList}
        categorias={categoriasFornecedor}
        onFornecedorChange={handleFornecedorChange}
        onDragEnd={handleDragEnd}
        onMergeItems={(keepItemId, removeItemId) => {
          if (setCronogramaCompra) {
            setCronogramaCompra((prev: any[]) => {
              return prev.filter(
                (item: { id: number }) => item.id !== removeItemId
              )
            })
          }

          toast({
            title: 'Items unificados',
            description: 'Item duplicado foi automaticamente unificado.',
            status: 'info',
            duration: 3000,
            isClosable: true
          })
        }}
        onAdd={(dia, fornecedorId, lojaId, categoriaCompactaId) => {
          // Verificar se já existe um item com a mesma combinação
          const itemExistente = cronogramaCompra.find(
            item =>
              item.diaDaSemana === dia &&
              item.fornecedorId === fornecedorId &&
              item.lojaId === lojaId &&
              item.categoriaCompactaId === categoriaCompactaId
          )

          if (itemExistente) {
            toast({
              title: 'Já cadastrado',
              description:
                'Esta combinação de fornecedor, loja e categoria compacta já está cadastrada para este dia da semana.',
              status: 'warning',
              duration: 3000,
              isClosable: true
            })
            return
          }

          const novoItem: ScheduleItem = {
            id: nextTempItemId(),
            empresaId: Number(companieId),
            usuarioId: selectedComprador || tokenUserId,
            usuarioEmail: selectedCompradorEmail || '',
            // Só o nome: o card já exibe "id - nome" (antes ficava "id - id - nome")
            fornecedorId,
            fornecedorNome:
              dataFornecedor.find((f: any) => f.fornecedorId === fornecedorId)
                ?.fornecedorNome ||
              stripIdPrefix(
                fornecedoresList.find(f => f.value === fornecedorId)?.label,
                fornecedorId
              ),
            lojaId,
            lojaNome: stripIdPrefix(
              centersConfig.find(l => l.value === lojaId)?.label,
              lojaId
            ),
            diaDaSemana: dia,
            categoriaCompactaId,
            categoriaCompactaNome: stripIdPrefix(
              categoriasFornecedor.find(c => c.value === categoriaCompactaId)
                ?.label,
              categoriaCompactaId
            )
          }

          handleCronogromaCompra(novoItem)
        }}
        onDelete={id => {
          const itemParaRemover = cronogramaCompra.find(item => item.id === id)
          if (itemParaRemover) {
            deleteCronogromaCompra(itemParaRemover)
          }
        }}
      />
    </Box>
  )
}
